import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

/*
  Faixa de oferta no topo da Riviera (tarefa a7586bc3, pedido do Fábio em 18/09).
  Ele mandou três artes de um avião puxando "seu lote com até 50% de desconto"; o
  João preferiu a faixa esticada e perguntou se só a faixa não basta, porque o
  hero já tem a animação do dia para a noite e um avião cruzando ficaria fora de
  tempo. O Fábio respondeu pedindo "uma banderola balançando". Por isso o padrão é
  a bandeirola sozinha, ondulando; o avião existe só como variante (`aviao`) para
  a comparação na página de aprovação.

  A ondulação é SMIL no próprio SVG (troca do `d` do contorno e do caminho do
  texto), sem JS por quadro: o texto segue a curva porque está num <textPath> que
  anima junto com o pano. Com prefers-reduced-motion a faixa fica parada.
*/

// Três fases da onda do pano; o texto usa a linha do meio de cada fase.
const PANO = [
  'M10,22 C160,8 300,36 450,22 C520,15 570,20 590,24 L590,86 C570,82 520,77 450,84 C300,98 160,70 10,84 Z',
  'M10,24 C160,36 300,8 450,24 C520,31 570,26 590,22 L590,84 C570,88 520,93 450,86 C300,70 160,98 10,86 Z',
  'M10,22 C160,8 300,36 450,22 C520,15 570,20 590,24 L590,86 C570,82 520,77 450,84 C300,98 160,70 10,84 Z',
];
const MEIO = [
  'M30,58 C170,42 300,70 440,56 C510,49 560,54 575,56',
  'M30,56 C170,70 300,42 440,58 C510,64 560,58 575,56',
  'M30,58 C170,42 300,70 440,56 C510,49 560,54 575,56',
];

const entra = keyframes`
  from { opacity: 0; transform: translateX(-18%); }
  to   { opacity: 1; transform: translateX(0); }
`;

const Wrap = styled.div`
  position: absolute;
  z-index: 3;
  left: 50%;
  top: 64px;
  /* 640 e não 560: o Fábio pediu a faixa "um pouco maior" (WhatsApp 25/09 e reunião 15/10). */
  width: min(96vw, 640px);
  transform: translateX(-50%);
  pointer-events: none;
  @media (min-width: 768px) { top: 84px; }
  .inner { animation: ${entra} 1.6s ease-out both; }
  svg { width: 100%; height: auto; display: block; filter: drop-shadow(0 6px 14px rgba(0,0,0,0.35)); }
  @media (prefers-reduced-motion: reduce) { .inner { animation: none; } }
`;

function useMovimento() {
  const [ok, setOk] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return undefined;
    const upd = () => setOk(!mq.matches);
    upd();
    mq.addEventListener?.('change', upd);
    return () => mq.removeEventListener?.('change', upd);
  }, []);
  return ok;
}

export default function FaixaOferta({ texto = 'seu lote com até 50% de desconto', variante = 'faixa' }) {
  const anima = useMovimento();
  const aviao = variante === 'aviao';
  const dur = '3.2s';
  // O avião entra à direita do pano, puxando-o por dois cabos.
  const vb = aviao ? '0 0 760 120' : '0 0 600 110';
  return (
    <Wrap aria-label={texto} role="img">
      <div className="inner">
        <svg viewBox={vb} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <defs>
            <linearGradient id="faixa-pano" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#e53935" />
              <stop offset="1" stopColor="#b71c1c" />
            </linearGradient>
            <path id="faixa-meio" d={MEIO[0]}>
              {anima && <animate attributeName="d" dur={dur} repeatCount="indefinite" values={MEIO.join(';')} />}
            </path>
          </defs>
          <path d={PANO[0]} fill="url(#faixa-pano)" stroke="#8e1414" strokeWidth="1.5">
            {anima && <animate attributeName="d" dur={dur} repeatCount="indefinite" values={PANO.join(';')} />}
          </path>
          <text fill="#fff" fontFamily="'Montserrat', 'Helvetica Neue', Arial, sans-serif"
            fontWeight="800" fontStyle="italic" fontSize="30" letterSpacing="0.5">
            <textPath href="#faixa-meio" startOffset="50%" textAnchor="middle" dominantBaseline="middle">
              {texto}
            </textPath>
          </text>
          {aviao && (
            <g>
              <path d="M590,26 L650,52 M590,84 L650,56" stroke="#eee" strokeWidth="1.6" fill="none" />
              <g transform="translate(648,30)">
                {/* avião simples de hélice, de lado, voando para a direita */}
                <path d="M4,26 L78,22 Q98,22 104,28 Q98,34 78,34 L4,32 Z" fill="#f5f5f5" stroke="#9e9e9e" />
                <path d="M34,24 L52,2 L62,2 L56,24 Z" fill="#e53935" />
                <path d="M36,32 L50,52 L60,52 L56,32 Z" fill="#e53935" />
                <path d="M4,26 L0,12 L10,12 L16,25 Z" fill="#e53935" />
                <circle cx="88" cy="26" r="3" fill="#90caf9" />
                <rect x="103" y="14" width="3" height="28" rx="1.5" fill="#616161">
                  {anima && <animateTransform attributeName="transform" type="scale" values="1 1;1 0.1;1 1" dur="0.12s" repeatCount="indefinite" additive="sum" />}
                </rect>
              </g>
            </g>
          )}
        </svg>
      </div>
    </Wrap>
  );
}
